import axios from 'axios';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { RoutingRedirectDTO, DnsCacheConfigDTO, DeviceGroupRefDTO } from '@webip/shared/src/dto/appliance';
import { PolicyDTO } from '@webip/shared/src/dto/firewall';
import { HotspotDTO } from '@webip/shared/src/dto/hotspot';
import { PortForwardRuleDTO } from '@webip/shared/src/dto/nat';
import { RadiusUserDTO, RadiusUserGroupDTO } from '@webip/shared/src/dto/radius_user';
import { VPNDTO } from '@webip/shared/src/dto/vpn';
import type { QosRespostaDTO } from '@webip/shared/src/dto/qos';
import { useAppliance } from './useAppliance';
import { useAuth } from './useAuth';
import type { AgenteDTO } from '@webip/shared/src/dto/agente';

type AgentStatus = {
    agentId: string;
    version: string;
    connectedAt: string;
    squidStatus?: { status: string; checkedAt: string };
};

type SquidBadgeState = 'active' | 'inactive' | 'checking' | 'error' | 'disabled';

export const useSidebarBadges = () => {
    const { applianceId, appliances, selectedAppliance } = useAppliance();
    // /api/agente exige GESTOR e a tela é ADMIN-only: sem este gate, todo
    // usuário comum dispararia um 403 a cada 30s só pra desenhar um badge que
    // ele nem vê.
    const { user } = useAuth();
    const podeVerAgentes = user?.papel === 'ADMIN';

    const appliesToSelectedAppliance = (group?: RadiusUserGroupDTO) => {
        if (!selectedAppliance?.id || !group) return false;

        const scope = group.appliance_scope ?? 'all';
        if (scope === 'all') return true;
        if (scope === 'none') return false;

        return (group.appliance_ids ?? []).includes(selectedAppliance.id);
    };

    const queryAgents = useQuery({
        queryKey: ['agents'],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; agents: AgentStatus[] }>('/api/appliance/agents', {
                withCredentials: true,
            });
            return res.data.agents ?? [];
        },
        staleTime: 10_000,
        refetchInterval: 10_000,
    });

    const queryAgentes = useQuery({
        queryKey: ['agentes'],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: AgenteDTO[] }>('/api/agente', {
                withCredentials: true,
            });
            return res.data.result ?? [];
        },
        enabled: podeVerAgentes,
        staleTime: 30_000,
        refetchInterval: 30_000,
    });

    const queryNatRules = useQuery({
        queryKey: ['nat_port_forward', applianceId],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: PortForwardRuleDTO[] }>(
                `/api/nat/port-forward?appliance_id=${applianceId}`,
                { withCredentials: true },
            );
            return res.data.result ?? [];
        },
        enabled: !!applianceId,
        staleTime: 30_000,
    });

    const queryVPNs = useQuery({
        queryKey: ['vpns', applianceId],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: VPNDTO[] }>(
                `/api/vpn?appliance_id=${applianceId}`,
                { withCredentials: true },
            );
            return res.data.result ?? [];
        },
        enabled: !!applianceId,
        staleTime: 30_000,
    });

    const queryHotspots = useQuery({
        queryKey: ['hotspot-list'],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: HotspotDTO[] }>(
                `/api/hotspot`,
                { withCredentials: true },
            );
            return res.data.result ?? [];
        },
        retry: false,
        staleTime: 30_000,
    });

    const queryRoutingRedirects = useQuery({
        queryKey: ['routing-redirect', applianceId],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: RoutingRedirectDTO[] }>(
                `/api/routing-redirect?appliance_id=${applianceId}`,
                { withCredentials: true },
            );
            return res.data.result ?? [];
        },
        enabled: !!applianceId,
        staleTime: 30_000,
    });

    const queryDnsCache = useQuery({
        queryKey: ['dns-config', applianceId],
        queryFn: async () => {
            try {
                const res = await axios.get<{ ok: boolean; result: DnsCacheConfigDTO }>(
                    `/api/appliance/${applianceId}/dns`,
                    { withCredentials: true },
                );
                return res.data.result ?? null;
            } catch (error: any) {
                if (error?.response?.status === 404) {
                    return null;
                }
                throw error;
            }
        },
        enabled: !!applianceId,
        retry: false,
        staleTime: 30_000,
    });

    // Chave própria: a QosPage preenche o formulário no onSuccess de
    // ['qos-config', id] — um refetch disparado daqui sobrescreveria edições
    // ainda não salvas.
    const queryQos = useQuery({
        queryKey: ['qos-config-sidebar', applianceId],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: QosRespostaDTO }>(
                `/api/appliance/${applianceId}/qos`,
                { withCredentials: true },
            );
            return res.data.result ?? null;
        },
        enabled: !!applianceId && podeVerAgentes,
        retry: false,
        staleTime: 30_000,
    });

    const queryFwPolicies = useQuery({
        queryKey: ['fw_policies_sidebar'],
        queryFn: async () => {
            const res = await axios.get<{ result: PolicyDTO[]; total: number; cursor: string | number | null }>(
                '/api/firewall/policies',
                { withCredentials: true },
            );
            return res.data;
        },
        staleTime: 60_000,
        refetchOnWindowFocus: false,
    });

    const queryDeviceGroups = useQuery({
        queryKey: ['grupo_dispositivo_sidebar'],
        queryFn: async () => {
            const res = await axios.get<{ result: DeviceGroupRefDTO[] }>(
                '/api/grupo_dispositivo',
                { withCredentials: true },
            );
            return res.data.result ?? [];
        },
        staleTime: 60_000,
        refetchOnWindowFocus: false,
    });

    const queryRadiusGroups = useQuery({
        queryKey: ['radius-groups'],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: RadiusUserGroupDTO[] }>(
                '/api/radius/groups',
                { withCredentials: true },
            );
            return res.data.result ?? [];
        },
        staleTime: 60_000,
        refetchOnWindowFocus: false,
    });

    const queryRadiusUsers = useQuery({
        queryKey: ['radius-users', ''],
        queryFn: async () => {
            const groups = queryRadiusGroups.data ?? [];
            if (groups.length === 0) {
                return [] as RadiusUserDTO[];
            }

            const results = await Promise.all(
                groups.map((group) =>
                    axios
                        .get<{ ok: boolean; result: RadiusUserDTO[] }>(
                            `/api/radius/groups/${group.id}/users`,
                            { withCredentials: true },
                        )
                        .then((res) => res.data.result ?? []),
                ),
            );

            return results.flat();
        },
        enabled: queryRadiusGroups.isSuccess,
        staleTime: 60_000,
        refetchOnWindowFocus: false,
    });

    return useMemo(() => {
        const lansCount = selectedAppliance?.redesLan?.length ?? null;
        const wansCount = selectedAppliance?.redesWan?.length ?? null;
        const devicesCount = selectedAppliance?.dispositivos?.length ?? null;
        const radiusGroupsMap = new Map((queryRadiusGroups.data ?? []).map((group) => [group.id, group]));
        const radiusUsers = queryRadiusUsers.data ?? [];
        const radiusUsersForSelectedAppliance = selectedAppliance?.id
            ? radiusUsers.filter((user) => appliesToSelectedAppliance(radiusGroupsMap.get(user.grupo_id))).length
            : null;

        const deviceGroupsMap = new Map((queryDeviceGroups.data ?? []).map((g) => [g.id, g]));
        const fwPolicies = queryFwPolicies.data?.result ?? [];
        const firewallPoliciesCount = queryFwPolicies.isSuccess ? (queryFwPolicies.data?.total ?? fwPolicies.length) : null;
        const firewallPoliciesForSelectedAppliance = selectedAppliance?.id
            ? fwPolicies.filter((policy) => {
                if (policy.scope === 'appliance') return policy.applianceId === selectedAppliance.id;
                if (policy.scope === 'grupos') {
                    return policy.grupoIds.some((gid) => deviceGroupsMap.get(gid)?.appliance_id === selectedAppliance.id);
                }
                return false;
            }).length
            : null;

        const vpns = queryVPNs.data ?? [];
        const vpnClientsCount = vpns.filter((vpn) => vpn.modo === 'cliente').length;
        const vpnServersCount = vpns.filter((vpn) => vpn.modo === 'servidor').length;

        const squidState = (() => {
            if (!selectedAppliance) return null;
            if (!selectedAppliance.squidSummary?.ativo) return 'disabled' as SquidBadgeState;

            const agent = (queryAgents.data ?? []).find((item) => item.agentId === selectedAppliance.agent_id);
            if (!agent?.squidStatus) return 'checking' as SquidBadgeState;
            if (agent.squidStatus.status === 'active') return 'active' as SquidBadgeState;
            if (agent.squidStatus.status === 'inactive') return 'inactive' as SquidBadgeState;
            if (agent.squidStatus.status === 'checking') return 'checking' as SquidBadgeState;
            return 'error' as SquidBadgeState;
        })();

        const dnsCacheEnabled = queryDnsCache.isSuccess
            ? Boolean(queryDnsCache.data?.ativo)
            : null;

        const agentes = queryAgentes.data ?? [];

        return {
            appliancesCount: appliances.length,
            agentesCount: queryAgentes.isSuccess ? agentes.length : null,
            agentesOnline: queryAgentes.isSuccess ? agentes.filter((a) => a.online).length : null,
            agentesNaoAdotados: queryAgentes.isSuccess ? agentes.filter((a) => !a.adotado).length : null,
            firewallPoliciesCount,
            firewallPoliciesForSelectedAppliance,
            radiusUsersCount: queryRadiusUsers.isSuccess ? radiusUsers.length : null,
            radiusUsersForSelectedAppliance,
            natCount: applianceId && queryNatRules.isSuccess ? (queryNatRules.data ?? []).length : null,
            vpnsCount: applianceId && queryVPNs.isSuccess ? vpns.length : null,
            vpnClientsCount: applianceId && queryVPNs.isSuccess ? vpnClientsCount : null,
            vpnServersCount: applianceId && queryVPNs.isSuccess ? vpnServersCount : null,
            devicesCount,
            hotspotsCount: queryHotspots.isSuccess ? (queryHotspots.data ?? []).length : null,
            hotspotsForSelectedAppliance: selectedAppliance?.id && queryHotspots.isSuccess
                ? new Set(
                    (selectedAppliance.redesLan ?? [])
                        .filter((l) => l.hotspot_id != null)
                        .map((l) => l.hotspot_id)
                  ).size
                : null,
            networksCount: lansCount != null && wansCount != null ? lansCount + wansCount : null,
            lansCount,
            wansCount,
            redirectsCount: applianceId && queryRoutingRedirects.isSuccess ? (queryRoutingRedirects.data ?? []).length : null,
            squidState,
            dnsCacheEnabled,
            dnsCacheErrored: queryDnsCache.isError,
            qosEnabled: queryQos.isSuccess ? Boolean(queryQos.data?.config.ativo) : null,
            qosErrored: queryQos.isError,
        };
    }, [
        applianceId,
        appliances.length,
        queryAgents.data,
        queryAgentes.data,
        queryAgentes.isSuccess,
        queryDnsCache.data,
        queryDnsCache.isError,
        queryDnsCache.isSuccess,
        queryDeviceGroups.data,
        queryQos.data,
        queryQos.isError,
        queryQos.isSuccess,
        queryFwPolicies.data,
        queryFwPolicies.isSuccess,
        queryHotspots.data,
        queryHotspots.isSuccess,
        selectedAppliance?.redesLan,
        queryNatRules.data,
        queryNatRules.isSuccess,
        queryRadiusGroups.data,
        queryRadiusUsers.data,
        queryRadiusUsers.isSuccess,
        queryRoutingRedirects.data,
        queryRoutingRedirects.isSuccess,
        queryVPNs.data,
        queryVPNs.isSuccess,
        selectedAppliance,
    ]);
};